import { QuestionGroup, QuestionGroupNode } from "@/hooks/useQuestionMappings";

/**
 * Migrate old format (array of strings) to new format (object with questions and subgroups)
 */
export function migrateGroupsFormat(groups: any): QuestionGroup {
  const migratedGroups: QuestionGroup = {};
  
  if (!groups || typeof groups !== 'object') {
    return {};
  }
  
  Object.entries(groups).forEach(([groupName, value]) => {
    // Check if it's old format (array of strings)
    if (Array.isArray(value)) {
      migratedGroups[groupName] = {
        questions: value as string[],
        subgroups: {}
      };
    } 
    // Check if it's new format but missing subgroups
    else if (value && typeof value === 'object' && 'questions' in value) {
      // Clean up corrupted question objects
      const cleanQuestions = (value as any).questions.map((q: any) => {
        if (typeof q === 'string') {
          return q;
        } else if (q && typeof q === 'object') {
          // If it's a corrupted object, extract the name
          if (q.name && typeof q.name === 'object') {
            return q.name.name || q.name;
          } else if (typeof q.name === 'string') {
            return q.name;
          }
        }
        return q;
      }).filter(Boolean);
      
      migratedGroups[groupName] = {
        questions: cleanQuestions,
        subgroups: migrateGroupsFormat((value as any).subgroups || {})
      };
    }
    // Invalid format, create empty node
    else {
      migratedGroups[groupName] = {
        questions: [],
        subgroups: {}
      };
    }
  });
  
  return migratedGroups;
}

/**
 * Get all question names from a group node and its subgroups recursively
 */
export function getAllQuestionsFromNode(node: QuestionGroupNode): string[] {
  const questions = node.questions.map(questionItem => {
    // Manejar tanto la estructura antigua (string) como la nueva (objeto)
    return typeof questionItem === 'string' ? questionItem : questionItem.name;
  });
  
  if (node.subgroups) {
    Object.values(node.subgroups).forEach(subgroup => {
      questions.push(...getAllQuestionsFromNode(subgroup));
    });
  }
  
  return questions;
}

/**
 * Get all question names from all groups recursively
 */
export function getAllQuestionsFromGroups(groups: QuestionGroup): string[] {
  const allQuestions: string[] = [];
  
  console.log('🔍 getAllQuestionsFromGroups DEBUG:');
  console.log('  - Groups input:', groups);
  
  Object.values(groups).forEach(node => {
    const nodeQuestions = getAllQuestionsFromNode(node);
    console.log('  - Node questions:', nodeQuestions);
    allQuestions.push(...nodeQuestions);
  });
  
  console.log('  - Final all questions:', allQuestions);
  return allQuestions;
}

/**
 * Count total questions in a group node recursively
 */
export function countQuestionsInNode(node: QuestionGroupNode): number {
  let count = node.questions.length;
  
  if (node.subgroups) {
    Object.values(node.subgroups).forEach(subgroup => {
      count += countQuestionsInNode(subgroup);
    });
  }
  
  return count;
}

/**
 * Count total subgroups in a group node recursively
 */
export function countSubgroupsInNode(node: QuestionGroupNode): number {
  if (!node.subgroups) return 0;
  
  let count = Object.keys(node.subgroups).length;
  
  Object.values(node.subgroups).forEach(subgroup => {
    count += countSubgroupsInNode(subgroup);
  });
  
  return count;
}

/**
 * Add questions to a specific path in the group hierarchy
 */
export function addQuestionsToPath(
  groups: QuestionGroup,
  path: string[],
  questionNames: string[]
): QuestionGroup {
  if (path.length === 0) return groups;
  
  const newGroups = { ...groups };
  const [currentGroup, ...remainingPath] = path;
  
  if (!newGroups[currentGroup]) {
    newGroups[currentGroup] = { questions: [], subgroups: {} };
  }
  
  if (remainingPath.length === 0) {
    // Add to current level
    const currentNode = newGroups[currentGroup];
    // Convert question names to objects with name and key
    const questionObjects = questionNames.map(name => ({ name, key: name }));
    newGroups[currentGroup] = {
      ...currentNode,
      questions: [...currentNode.questions, ...questionObjects]
    };
  } else {
    // Navigate deeper
    const currentNode = { ...newGroups[currentGroup] };
    if (!currentNode.subgroups) {
      currentNode.subgroups = {};
    }
    
    currentNode.subgroups = addQuestionsToPathRecursive(
      currentNode.subgroups,
      remainingPath,
      questionNames
    );
    
    newGroups[currentGroup] = currentNode;
  }
  
  return newGroups;
}

function addQuestionsToPathRecursive(
  subgroups: { [key: string]: QuestionGroupNode },
  path: string[],
  questionNames: string[]
): { [key: string]: QuestionGroupNode } {
  const [currentGroup, ...remainingPath] = path;
  const newSubgroups = { ...subgroups };
  
  if (!newSubgroups[currentGroup]) {
    newSubgroups[currentGroup] = { questions: [], subgroups: {} };
  }
  
  if (remainingPath.length === 0) {
    const currentNode = newSubgroups[currentGroup];
    // Convert question names to objects with name and key
    const questionObjects = questionNames.map(name => ({ name, key: name }));
    newSubgroups[currentGroup] = {
      ...currentNode,
      questions: [...currentNode.questions, ...questionObjects]
    };
  } else {
    const currentNode = { ...newSubgroups[currentGroup] };
    if (!currentNode.subgroups) {
      currentNode.subgroups = {};
    }
    
    currentNode.subgroups = addQuestionsToPathRecursive(
      currentNode.subgroups,
      remainingPath,
      questionNames
    );
    
    newSubgroups[currentGroup] = currentNode;
  }
  
  return newSubgroups;
}

/**
 * Remove a question from a specific path in the group hierarchy
 */
export function removeQuestionFromPath(
  groups: QuestionGroup,
  path: string[],
  questionName: string
): QuestionGroup {
  if (path.length === 0) return groups;
  
  const newGroups = { ...groups };
  const [currentGroup, ...remainingPath] = path;
  
  if (!newGroups[currentGroup]) return groups;
  
  if (remainingPath.length === 0) {
    const currentNode = newGroups[currentGroup];
    newGroups[currentGroup] = {
      ...currentNode,
      questions: currentNode.questions.filter(q => {
        const qName = typeof q === 'string' ? q : q.name;
        return qName !== questionName;
      })
    };
  } else {
    const currentNode = { ...newGroups[currentGroup] };
    if (currentNode.subgroups) {
      currentNode.subgroups = removeQuestionFromPathRecursive(
        currentNode.subgroups,
        remainingPath,
        questionName
      );
      newGroups[currentGroup] = currentNode;
    }
  }
  
  return newGroups;
}

function removeQuestionFromPathRecursive(
  subgroups: { [key: string]: QuestionGroupNode },
  path: string[],
  questionName: string
): { [key: string]: QuestionGroupNode } {
  const [currentGroup, ...remainingPath] = path;
  const newSubgroups = { ...subgroups };
  
  if (!newSubgroups[currentGroup]) return subgroups;
  
  if (remainingPath.length === 0) {
    const currentNode = newSubgroups[currentGroup];
    newSubgroups[currentGroup] = {
      ...currentNode,
      questions: currentNode.questions.filter(q => {
        const qName = typeof q === 'string' ? q : q.name;
        return qName !== questionName;
      })
    };
  } else {
    const currentNode = { ...newSubgroups[currentGroup] };
    if (currentNode.subgroups) {
      currentNode.subgroups = removeQuestionFromPathRecursive(
        currentNode.subgroups,
        remainingPath,
        questionName
      );
      newSubgroups[currentGroup] = currentNode;
    }
  }
  
  return newSubgroups;
}

/**
 * Delete a group or subgroup at a specific path
 */
export function deleteGroupAtPath(
  groups: QuestionGroup,
  path: string[]
): QuestionGroup {
  if (path.length === 0) return groups;
  
  const newGroups = { ...groups };
  const [currentGroup, ...remainingPath] = path;
  
  if (remainingPath.length === 0) {
    delete newGroups[currentGroup];
  } else {
    const currentNode = { ...newGroups[currentGroup] };
    if (currentNode.subgroups) {
      currentNode.subgroups = deleteSubgroupAtPathRecursive(
        currentNode.subgroups,
        remainingPath
      );
      newGroups[currentGroup] = currentNode;
    }
  }
  
  return newGroups;
}

function deleteSubgroupAtPathRecursive(
  subgroups: { [key: string]: QuestionGroupNode },
  path: string[]
): { [key: string]: QuestionGroupNode } {
  const [currentGroup, ...remainingPath] = path;
  const newSubgroups = { ...subgroups };
  
  if (remainingPath.length === 0) {
    delete newSubgroups[currentGroup];
  } else {
    const currentNode = { ...newSubgroups[currentGroup] };
    if (currentNode.subgroups) {
      currentNode.subgroups = deleteSubgroupAtPathRecursive(
        currentNode.subgroups,
        remainingPath
      );
      newSubgroups[currentGroup] = currentNode;
    }
  }
  
  return newSubgroups;
}

/**
 * Create a new group or subgroup at a specific path
 */
export function createGroupAtPath(
  groups: QuestionGroup,
  path: string[],
  groupName: string
): QuestionGroup {
  if (path.length === 0) {
    // Create at root level
    if (groups[groupName]) {
      throw new Error("Ya existe un grupo con ese nombre");
    }
    return {
      ...groups,
      [groupName]: { questions: [], subgroups: {} }
    };
  }
  
  const newGroups = { ...groups };
  const [currentGroup, ...remainingPath] = path;
  
  if (!newGroups[currentGroup]) {
    throw new Error("El grupo padre no existe");
  }
  
  const currentNode = { ...newGroups[currentGroup] };
  if (!currentNode.subgroups) {
    currentNode.subgroups = {};
  }
  
  currentNode.subgroups = createSubgroupAtPathRecursive(
    currentNode.subgroups,
    remainingPath,
    groupName
  );
  
  newGroups[currentGroup] = currentNode;
  return newGroups;
}

function createSubgroupAtPathRecursive(
  subgroups: { [key: string]: QuestionGroupNode },
  path: string[],
  groupName: string
): { [key: string]: QuestionGroupNode } {
  if (path.length === 0) {
    if (subgroups[groupName]) {
      throw new Error("Ya existe un subgrupo con ese nombre");
    }
    return {
      ...subgroups,
      [groupName]: { questions: [], subgroups: {} }
    };
  }
  
  const [currentGroup, ...remainingPath] = path;
  const newSubgroups = { ...subgroups };
  
  if (!newSubgroups[currentGroup]) {
    throw new Error("El grupo padre no existe");
  }
  
  const currentNode = { ...newSubgroups[currentGroup] };
  if (!currentNode.subgroups) {
    currentNode.subgroups = {};
  }
  
  currentNode.subgroups = createSubgroupAtPathRecursive(
    currentNode.subgroups,
    remainingPath,
    groupName
  );
  
  newSubgroups[currentGroup] = currentNode;
  return newSubgroups;
}

/**
 * Rename a group or subgroup at a specific path
 */
export function renameGroupAtPath(
  groups: QuestionGroup,
  path: string[],
  newName: string
): QuestionGroup {
  if (path.length === 0) return groups;
  
  const newGroups = { ...groups };
  const [currentGroup, ...remainingPath] = path;
  
  if (remainingPath.length === 0) {
    // Rename at root level
    if (newGroups[newName]) {
      throw new Error("Ya existe un grupo con ese nombre");
    }
    newGroups[newName] = newGroups[currentGroup];
    delete newGroups[currentGroup];
  } else {
    const currentNode = { ...newGroups[currentGroup] };
    if (currentNode.subgroups) {
      currentNode.subgroups = renameSubgroupAtPathRecursive(
        currentNode.subgroups,
        remainingPath,
        newName
      );
      newGroups[currentGroup] = currentNode;
    }
  }
  
  return newGroups;
}

function renameSubgroupAtPathRecursive(
  subgroups: { [key: string]: QuestionGroupNode },
  path: string[],
  newName: string
): { [key: string]: QuestionGroupNode } {
  const [currentGroup, ...remainingPath] = path;
  const newSubgroups = { ...subgroups };
  
  if (remainingPath.length === 0) {
    if (newSubgroups[newName]) {
      throw new Error("Ya existe un subgrupo con ese nombre");
    }
    newSubgroups[newName] = newSubgroups[currentGroup];
    delete newSubgroups[currentGroup];
  } else {
    const currentNode = { ...newSubgroups[currentGroup] };
    if (currentNode.subgroups) {
      currentNode.subgroups = renameSubgroupAtPathRecursive(
        currentNode.subgroups,
        remainingPath,
        newName
      );
      newSubgroups[currentGroup] = currentNode;
    }
  }
  
  return newSubgroups;
}
